// Admin Plans Tab Component
import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Plus, Edit2, Trash2, Users, Building2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAdmin, API_URL } from './AdminContext';

export function AdminPlansTab() {
  const { plans, fetchPlans } = useAdmin();
  const [showPlanDialog, setShowPlanDialog] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [planForm, setPlanForm] = useState({
    name: '', display_name: '', price: '', billing_cycle: 'monthly',
    max_users: '', max_clients: '', max_matches: '', max_sessions: '', features: '',
    is_free: false, free_trial_days: '7',
    feature_reference_commission: false, feature_patti: false, feature_limit: false,
    feature_monday_settling: false, feature_mcx_fo: false
  });

  const openEditPlan = (plan) => {
    setEditingPlan(plan);
    setPlanForm({
      name: plan.name || '',
      display_name: plan.display_name || '',
      price: plan.price?.toString() || '',
      billing_cycle: plan.billing_cycle || 'monthly',
      max_users: plan.max_users?.toString() || '',
      max_clients: plan.max_clients?.toString() || '',
      max_matches: plan.max_matches?.toString() || '',
      max_sessions: plan.max_sessions?.toString() || '',
      features: plan.features?.join(', ') || '',
      is_free: plan.is_free || false,
      free_trial_days: plan.free_trial_days?.toString() || '7',
      feature_reference_commission: plan.feature_toggles?.reference_commission || false,
      feature_patti: plan.feature_toggles?.patti || false,
      feature_limit: plan.feature_toggles?.limit || false,
      feature_monday_settling: plan.feature_toggles?.monday_settling || false,
      feature_mcx_fo: plan.feature_toggles?.mcx_fo || false
    });
    setShowPlanDialog(true);
  };

  const handleSavePlan = async () => {
    try {
      const planData = {
        name: planForm.name,
        display_name: planForm.display_name,
        price: parseFloat(planForm.price) || 0,
        billing_cycle: planForm.billing_cycle,
        max_users: parseInt(planForm.max_users) || 1,
        max_clients: parseInt(planForm.max_clients) || 10,
        max_matches: parseInt(planForm.max_matches) || -1,
        max_sessions: parseInt(planForm.max_sessions) || 1,
        features: planForm.features.split(',').map(f => f.trim()).filter(Boolean),
        is_free: planForm.is_free,
        free_trial_days: parseInt(planForm.free_trial_days) || 0,
        feature_toggles: {
          reference_commission: planForm.feature_reference_commission,
          patti: planForm.feature_patti,
          limit: planForm.feature_limit,
          monday_settling: planForm.feature_monday_settling,
          mcx_fo: planForm.feature_mcx_fo
        }
      };

      const url = editingPlan 
        ? `${API_URL}/api/admin/plans/${editingPlan.plan_id}`
        : `${API_URL}/api/admin/plans`;
      
      const response = await fetch(url, {
        method: editingPlan ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(planData)
      });

      if (response.ok) {
        toast.success(editingPlan ? 'Plan updated' : 'Plan created');
        setShowPlanDialog(false);
        fetchPlans();
      } else {
        toast.error('Failed to save plan');
      }
    } catch (error) {
      toast.error('Failed to save plan');
    }
  };

  const handleDeletePlan = async (planId) => {
    if (!confirm('Are you sure you want to delete this plan?')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/admin/plans/${planId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      if (response.ok) {
        toast.success('Plan deleted');
        fetchPlans();
      } else {
        toast.error('Failed to delete plan');
      }
    } catch (error) {
      toast.error('Failed to delete plan');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-lg font-semibold">Subscription Plans</h2>
        <Button size="sm" onClick={() => { 
          setEditingPlan(null); 
          setPlanForm({ 
            name: '', display_name: '', price: '', billing_cycle: 'monthly', 
            max_users: '', max_clients: '', max_matches: '', max_sessions: '', features: '',
            is_free: false, free_trial_days: '7',
            feature_reference_commission: false, feature_patti: false, feature_limit: false,
            feature_monday_settling: false, feature_mcx_fo: false
          }); 
          setShowPlanDialog(true); 
        }}>
          <Plus className="w-4 h-4 mr-2" />
          Add Plan
        </Button>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {plans.map(plan => (
          <div key={plan.plan_id} className="p-4 rounded-lg border border-gray-700 bg-gray-800/50">
            <div className="flex justify-between items-start mb-3">
              <div>
                <h3 className="font-semibold text-lg">{plan.display_name}</h3>
                <p className="text-2xl font-bold text-yellow-500">
                  ₹{plan.price}<span className="text-sm text-gray-400">/{plan.billing_cycle}</span>
                </p>
              </div>
              <div className="flex gap-1">
                <Button variant="ghost" size="sm" onClick={() => openEditPlan(plan)}>
                  <Edit2 className="w-4 h-4" />
                </Button>
                <Button variant="ghost" size="sm" onClick={() => handleDeletePlan(plan.plan_id)}>
                  <Trash2 className="w-4 h-4 text-red-500" />
                </Button>
              </div>
            </div>
            <div className="space-y-2 text-sm">
              <p className="text-gray-400">
                <Users className="w-4 h-4 inline mr-1" />
                {plan.max_users === -1 ? 'Unlimited' : plan.max_users} users
              </p>
              <p className="text-gray-400">
                <Building2 className="w-4 h-4 inline mr-1" />
                {plan.max_clients === -1 ? 'Unlimited' : plan.max_clients} clients
              </p>
              <div className="pt-2 border-t border-gray-700">
                {plan.features?.map((f, i) => (
                  <p key={i} className="text-xs text-gray-500">• {f}</p>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Plan Dialog */}
      <Dialog open={showPlanDialog} onOpenChange={setShowPlanDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingPlan ? 'Edit Plan' : 'Create New Plan'}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-4">
            <div>
              <Label>Name (internal)</Label>
              <Input value={planForm.name} onChange={(e) => setPlanForm({...planForm, name: e.target.value})} placeholder="starter" />
            </div>
            <div>
              <Label>Display Name</Label>
              <Input value={planForm.display_name} onChange={(e) => setPlanForm({...planForm, display_name: e.target.value})} placeholder="Starter Plan" />
            </div>
            <div>
              <Label>Price (₹)</Label>
              <Input type="number" value={planForm.price} onChange={(e) => setPlanForm({...planForm, price: e.target.value})} placeholder="499" />
            </div>
            <div>
              <Label>Billing Cycle</Label>
              <Select value={planForm.billing_cycle} onValueChange={(v) => setPlanForm({...planForm, billing_cycle: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="monthly">Monthly</SelectItem>
                  <SelectItem value="yearly">Yearly</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Max Users</Label>
              <Input type="number" value={planForm.max_users} onChange={(e) => setPlanForm({...planForm, max_users: e.target.value})} placeholder="-1 for unlimited" />
            </div>
            <div>
              <Label>Max Clients</Label>
              <Input type="number" value={planForm.max_clients} onChange={(e) => setPlanForm({...planForm, max_clients: e.target.value})} placeholder="-1 for unlimited" />
            </div>
            <div>
              <Label>Max Matches</Label>
              <Input type="number" value={planForm.max_matches} onChange={(e) => setPlanForm({...planForm, max_matches: e.target.value})} placeholder="-1 for unlimited" />
            </div>
            <div>
              <Label>Max Sessions</Label>
              <Input type="number" value={planForm.max_sessions} onChange={(e) => setPlanForm({...planForm, max_sessions: e.target.value})} placeholder="1" />
            </div>
            <div className="col-span-2">
              <Label>Features (comma separated)</Label>
              <Input value={planForm.features} onChange={(e) => setPlanForm({...planForm, features: e.target.value})} placeholder="Feature 1, Feature 2" />
            </div>
            <div className="flex items-center gap-2">
              <Checkbox checked={planForm.is_free} onCheckedChange={(v) => setPlanForm({...planForm, is_free: v})} />
              <Label>Is Free Plan</Label>
            </div>
            <div>
              <Label>Free Trial Days</Label>
              <Input type="number" value={planForm.free_trial_days} onChange={(e) => setPlanForm({...planForm, free_trial_days: e.target.value})} />
            </div>
            <div className="col-span-2 border-t pt-4 mt-2">
              <Label className="text-sm font-semibold mb-2 block">Feature Toggles</Label>
              <div className="grid grid-cols-3 gap-2">
                <div className="flex items-center gap-2">
                  <Checkbox checked={planForm.feature_reference_commission} onCheckedChange={(v) => setPlanForm({...planForm, feature_reference_commission: v})} />
                  <Label className="text-xs">Reference Commission</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox checked={planForm.feature_patti} onCheckedChange={(v) => setPlanForm({...planForm, feature_patti: v})} />
                  <Label className="text-xs">Patti</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox checked={planForm.feature_limit} onCheckedChange={(v) => setPlanForm({...planForm, feature_limit: v})} />
                  <Label className="text-xs">Limit</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox checked={planForm.feature_monday_settling} onCheckedChange={(v) => setPlanForm({...planForm, feature_monday_settling: v})} />
                  <Label className="text-xs">Monday Settling</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox checked={planForm.feature_mcx_fo} onCheckedChange={(v) => setPlanForm({...planForm, feature_mcx_fo: v})} />
                  <Label className="text-xs">MCX/F&O</Label>
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPlanDialog(false)}>Cancel</Button>
            <Button onClick={handleSavePlan}>{editingPlan ? 'Update' : 'Create'} Plan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
